import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/utils/cn'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', isLoading = false, disabled, children, ...props },
  ref
) {
  const baseStyles = cn(
    'inline-flex items-center justify-center font-medium',
    'rounded-md transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50'
  )

  const variantStyles = {
    primary: cn('bg-primary-600 text-white', 'hover:bg-primary-700', 'focus:ring-primary-500'),
    secondary: cn(
      'border border-gray-300 bg-white text-gray-700',
      'hover:bg-gray-50',
      'focus:ring-primary-500'
    ),
    ghost: cn('text-gray-700', 'hover:bg-gray-100', 'focus:ring-primary-500'),
    danger: cn('bg-error-500 text-white', 'hover:bg-error-600', 'focus:ring-error-500'),
  }

  const sizeStyles = {
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4 text-sm',
    lg: 'h-12 px-6 text-base',
  }

  return (
    <button
      ref={ref}
      className={cn(baseStyles, variantStyles[variant], sizeStyles[size], className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading && (
        <span
          className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  )
})
